import { useAuth, useClerk } from "@clerk/clerk-react";
import { useState, useEffect } from "react";

interface PlanFeature {
  id?: string;
  pathRoot?: string;
  name?: string;
  description?: string;
  slug?: string;
  avatarUrl?: string;
}

interface Plan {
  id?: string;
  pathRoot?: string;
  name: string; // Keep name required as it's essential
  amount?: number;
  amountFormatted?: string;
  annualAmount?: number;
  annualAmountFormatted?: string;
  annualMonthlyAmount?: number;
  annualMonthlyAmountFormatted?: string;
  currencySymbol?: string;
  currency?: string;
  description?: string;
  isDefault?: boolean;
  isRecurring?: boolean;
  hasBaseFee?: boolean;
  forPayerType?: string;
  publiclyVisible?: boolean;
  slug?: string;
  avatarUrl?: string;
  features?: PlanFeature[];
}

interface PlansResponse {
  total_count: number;
  data: Plan[];
}

export interface SubscriptionInfo {
  planName: string;
  price: number;
  period: "month" | "year";
  isLoaded: boolean;
  hasActivePlan: boolean;
  plan?: Plan | null; // Full plan object from Clerk
}

export const useSubscription = (): SubscriptionInfo => {
  const { has, isLoaded } = useAuth();
  const clerk = useClerk();

  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo>({
    planName: "",
    price: 0,
    period: "month",
    isLoaded: false,
    hasActivePlan: false,
    plan: null,
  });

  useEffect(() => {
    const fetchSubscriptionInfo = async () => {
      if (!isLoaded) return;

      try {
        const plansResponse = await clerk.billing.getPlans();
        const plans =
          (plansResponse as unknown as PlansResponse).data || plansResponse; // Handle both response structures

        const activePlan = plans.find((plan: Plan) => {
          return has?.({ plan: plan.slug || "" });
        });

        if (activePlan) {
          setSubscriptionInfo({
            planName: activePlan.name,
            price: (activePlan.amount || 0) / 100, // Convert from cents to dollars, handle undefined
            period: "month", // Default to month since the API doesn't specify billing period
            isLoaded: true,
            hasActivePlan: true,
            plan: activePlan, // Return the entire plan object
          });
        } else {
          setSubscriptionInfo({
            planName: "",
            price: 0,
            period: "month",
            isLoaded: true,
            hasActivePlan: false,
            plan: null,
          });
        }
      } catch (error) {
        console.error("Error fetching subscription info:", error);
        setSubscriptionInfo({
          planName: "",
          price: 0,
          period: "month",
          isLoaded: true,
          hasActivePlan: false,
          plan: null,
        });
      }
    };

    fetchSubscriptionInfo();
  }, [has, isLoaded, clerk]);

  return subscriptionInfo;
};
